import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { financeOperatorAPI } from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import FinancePaymentTable from '../payment/FinancePaymentTable';
import Loader from '../common/Loader';

const FinanceOperatorDashboard = () => {
  const { isDarkMode } = useAdminTheme();
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchPendingApprovalPayments = async (page = 1, search = '') => {
    try {
      setIsLoading(true);
      const response = await financeOperatorAPI.getPendingApprovalPayments({
        page,
        limit: 10,
        search
      });

      if (response.success) {
        setPayments(response.data.paymentRequests);
        setTotalPages(response.data.pagination.totalPages);
        setTotalCount(response.data.pagination.totalCount);
      } else {
        toast.error('Failed to fetch pending approval payments');
      }
    } catch (error) {
      console.error('Error fetching pending approval payments:', error);
      toast.error('Failed to fetch pending approval payments');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingApprovalPayments(currentPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleRefresh = () => {
    fetchPendingApprovalPayments(currentPage, searchTerm);
  };

  const handleApproval = () => {
    fetchPendingApprovalPayments(currentPage, searchTerm);
  };

  return (
    <div className={`max-w-8xl mx-auto px-4 sm:px-5 lg:px-6 py-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className={`text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Pending Approval Payments</h1>
          <p className={`mt-2 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Review and approve payments awaiting finance operator verification
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className={`flex items-center px-4 py-2 rounded-lg border transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
            isDarkMode 
              ? 'bg-skyone-card-dark border-skyone-border-dark text-gray-200 hover:bg-skyone-inset-dark' 
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
          title="Refresh payment list"
        >
          <svg 
            className={`w-5 h-5 mr-2 ${isLoading ? 'animate-spin' : ''}`}
            fill="none" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Search */}
      <div className={`mb-6 rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
      }`}>
        <div className="relative">
          <input
            type="text"
            placeholder="Search by client name, email, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setCurrentPage(1);
                fetchPendingApprovalPayments(1, searchTerm);
              }
            }}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-skyone-inset-dark border-skyone-border-dark text-gray-100 placeholder-gray-500' 
                : 'border-gray-300 bg-white text-gray-900'
            }`}
          />
          <svg 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Payment Table */}
      <div>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader size="lg" text="Loading payments..." isDarkMode={isDarkMode} />
          </div>
        ) : payments.length === 0 ? (
          <div className={`text-center py-12 rounded-lg border transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
          }`}>
            <div className={`mx-auto h-12 w-12 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className={`mt-2 text-sm font-medium transition-colors duration-300 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>No pending approval payments</h3>
            <p className={`mt-1 text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              All payments have been processed.
            </p>
          </div>
        ) : (
          <FinancePaymentTable
            payments={payments}
            onRefresh={handleRefresh}
            onApproval={handleApproval}
          />
        )}
      </div>

      {/* Pagination */}
      {!isLoading && payments.length > 0 && (
        <div className={`mt-6 flex items-center justify-between rounded-lg border p-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-sm transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalCount)} of {totalCount} payments
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                isDarkMode 
                  ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <span className={`px-3 py-1 text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 ${
                isDarkMode 
                  ? 'border-skyone-border-dark text-zinc-300 hover:bg-skyone-inset-dark' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceOperatorDashboard;

